import React from 'react';
import { i18n } from 'config/locales';

import { FinancialCardAbuseItemModel } from 'forms/sections/financial/card/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Text from 'components/Form/Text';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import { isPVQForm } from 'helpers/form';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation';
import NotApplicable from 'components/Form/NotApplicable';
import { travelCardActionOptions, travelCardDisciplinaryOptions } from 'constants/enums/financialOptions';
import MultipleDropdown from 'components/Form/MultipleDropdown';
import Layouts from 'components/Form/Location/Layouts';
import _ from 'lodash';

interface Props extends Partial<FinancialCardAbuseItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  requireFinancialCardDisciplinaryDate?: boolean
  dispatch?: Dispatch
  formType?: string
  addressBooks?: CommonAddressBooks
}

export default class CardItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialCardAbuseItemModel>) => {
    this.props.onUpdate({
      Agency: this.props.Agency,
      Address: this.props.Address,
      WhenDisciplineReceived: this.props.WhenDisciplineReceived,
      Date: this.props.Date,
      Reason: this.props.Reason,
      Amount: this.props.Amount,
      Description: this.props.Description,
      AmountEstimated: this.props.AmountEstimated,
      DisciplinaryAction: this.props.DisciplinaryAction,
      DisciplinaryActionExplanation: this.props.DisciplinaryActionExplanation,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenReason: this.props.ActionsTakenReason,
      AccountNum: this.props.AccountNum,
      ...queue,
    });
  };

  updateAgency = (values: FinancialCardAbuseItemModel['Agency']) => {
    this.update({
      Agency: values,
    });
  };

  updateAddress = (values: FinancialCardAbuseItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateDate = (values: FinancialCardAbuseItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateReason = (values: FinancialCardAbuseItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateAmount = (values: FinancialCardAbuseItemModel['Amount']) => {
    this.update({
      Amount: values,
    });
  };

  updateAmountEstimated = (values: FinancialCardAbuseItemModel['AmountEstimated']) => {
    this.update({
      AmountEstimated: values,
    });
  };

  updateDescription = (values: FinancialCardAbuseItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateDisciplinaryAction = (values: FinancialCardAbuseItemModel['DisciplinaryAction']) => {
    const includesOther = values?.value?.includes('Other');
    const includesNone = values?.value?.includes('None');

    if (includesNone && values?.value?.length === 1) {
      this.update({
        DisciplinaryAction: { values: ['None'] },
        DisciplinaryActionExplanation: {}
      });
    } else {
      this.update({
        DisciplinaryAction: { values: values?.value?.filter?.((i) => i !== 'None') },
        DisciplinaryActionExplanation: includesOther ? this.props.DisciplinaryActionExplanation : {}
      });
    }
  };

  updateActionsTaken = (values: FinancialCardAbuseItemModel['ActionsTaken']) => {
    const includesOther = values?.value?.includes('Other');
    const includesNone = values?.value?.includes('None');

    if (includesNone && values?.value?.length === 1) {
      this.update({
        ActionsTaken: { values: ['None'] },
        ActionsTakenReason: {}
      });
    } else {
      this.update({
        ActionsTaken: { values: values?.value?.filter?.((i) => i !== 'None') },
        ActionsTakenReason: includesOther ? this.props.ActionsTakenReason : {}
      });
    }
  };

  updateActionsTakenReason = (values: FinancialCardAbuseItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  updateWhenDisciplineReceived = (values: FinancialCardAbuseItemModel['WhenDisciplineReceived']) => {
    this.update({
      WhenDisciplineReceived: values,
    });
  };

  updateDisciplinaryActionExplanation = (values: FinancialCardAbuseItemModel['DisciplinaryActionExplanation']) => {
    this.update({
      DisciplinaryActionExplanation: values,
    });
  };

  updateAccountNum = (values: FinancialCardAbuseItemModel['AccountNum']) => {
    this.update({
      AccountNum: values,
    });
  };

  render() {
    const { requireFinancialCardDisciplinaryDate, formType } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(formType);

    return isPVQ ? (
      <div className="card-item">
        <Field
          title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.agencyNameText')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
        >
          <Text
            name="Agency"
            {...this.props.Agency}
            className="card-agency"
            required={this.props.required}
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            attribute={`${attribute}.Agency`}
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <PVQLocation
            {...this.props.Address}
            name="AgencyAddressComponent"
            label={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.branch.title')}
            domesticTitle={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.branch.ifYes.agencyLocation')}
            foreignTitle={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.branch.ifNo.agencyLocation')}
            layout={Layouts.ADDRESS}
            showMilitaryInstallation
            hideInstallationNameUnknownWhenForeign
            showApoFpoDpoPostcodeWhenForeign
            validated
            className="card-address"
            required={this.props.required}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.disciplineCheckboxGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DisciplinaryAction`}
        >
          <MultipleDropdown
            value={this.props.DisciplinaryAction?.values}
            attribute={`${attribute}.DisciplinaryAction`}
            onError={this.props.onError}
            scrollIntoView={this.props.scrollIntoView}
            onUpdate={this.updateDisciplinaryAction}
            required={this.props.required}
          >
            {travelCardDisciplinaryOptions.asOptionsValues()}
          </MultipleDropdown>
          {(this.props.DisciplinaryAction?.values?.includes('Other') || this.props.DisciplinaryAction?.values?.includes('None')) && (
            <Field
              title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.disciplineCheckboxGroup.ifNoActionsOrOtherTextArea')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DisciplinaryActionExplanation`}
            >
              <Textarea
                name="DisciplinaryActionExplanation"
                {...this.props.DisciplinaryActionExplanation}
                className="disciplinary-actions"
                required={this.props.required}
                onUpdate={this.updateDisciplinaryActionExplanation}
                onError={this.props.onError}
                attribute={`${attribute}.DisciplinaryActionExplanation`}
              />
            </Field>
          )}
        </Field>

        <Field
          title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.whenDateEstimated')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.WhenDisciplineReceived`}
        >
          <DateControl
            name="whendisciplinereceived"
            {...this.props.WhenDisciplineReceived}
            className="when-discipline-received"
            hideDay
            required={this.props.required}
            onUpdate={this.updateWhenDisciplineReceived}
            onError={this.props.onError}
            showEstimated={true}
            attribute={`${attribute}.WhenDisciplineReceived`}
          />
        </Field>

        <Field
          attribute={`${attribute}.Amount`}
          title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.amountOwedCurrencyEstimated')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <div>
            <Currency
              maxLength="10"
              attribute={`${attribute}.Amount`}
              name="Amount"
              {...this.props.Amount}
              className="card-amount"
              required={this.props.required}
              onUpdate={this.updateAmount}
              onError={this.props.onError}
            />
            <div className="flags">
              <Checkbox
                attribute={`${attribute}.AmountEstimated`}
                name="AmountEstimated"
                className="card-estimated"
                {...this.props.AmountEstimated}
                label={i18n.t('financial.pvq.shared.estimated')}
                toggle="false"
                onUpdate={this.updateAmountEstimated}
                onError={this.props.onError}
              />
            </div>
          </div>
        </Field>

        <Field
          attribute={`${attribute}.Description`}
          title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.additionalDetailsTextArea')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Description`}
            name="Description"
            {...this.props.Description}
            className="card-description"
            required={this.props.required}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.actionsTakenCheckboxGroup.title')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ActionsTaken`}
        >
          <MultipleDropdown
            value={this.props.ActionsTaken?.values}
            attribute={`${attribute}.ActionsTaken`}
            onError={this.props.onError}
            scrollIntoView={this.props.scrollIntoView}
            onUpdate={this.updateActionsTaken}
            required={this.props.required}
          >
            {travelCardActionOptions.map((act) => (
              <option key={act.key} value={act.value}>
                {act.text}
              </option>
            ))}
          </MultipleDropdown>
        </Field>

        {(this.props.ActionsTaken?.values?.includes('Other') || this.props.ActionsTaken?.values?.includes('None')) && (
          <Field
            title={i18n.t('financial.pvq.violatedCardTerm.branch.ifYes.actionsTakenCheckboxGroup.ifOther')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTakenReason`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTakenReason`}
              scrollIntoView={this.props.scrollIntoView}
              onError={this.props.onError}
              onUpdate={this.updateActionsTakenReason}
              required={this.props.required}
            />
          </Field>
        )}
      </div>
    ) : (
      <div className="card-item">
        <Field
          title={i18n.t('financial.card.heading.agency')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
        >
          <Text
            name="Agency"
            {...this.props.Agency}
            className="card-agency"
            required={this.props.required}
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            attribute={`${attribute}.Agency`}
          />
        </Field>

        <Field
          title={i18n.t('financial.card.heading.address')}
          optional
          help="financial.card.help.address"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="address"
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            className="card-address"
            layout={Location.Address}
            geocode
            dispatch={this.props.dispatch}
            addressBooks={this.props.addressBooks}
            addressBook="Agency"
            required={this.props.required}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            attribute={`${attribute}.Address`}
          />
        </Field>
        {requireFinancialCardDisciplinaryDate && (
          <Field
            title={i18n.t('financial.card.heading.date')}
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            shrink
            attribute={`${attribute}.Date`}
          >
            <DateControl
              name="Date"
              {...this.props.Date}
              className="card-date"
              hideDay
              minDateEqualTo
              required={this.props.required}
              onUpdate={this.updateDate}
              onError={this.props.onError}
              attribute={`${attribute}.Date`}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.card.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            attribute={`${attribute}.Reason`}
            name="Reason"
            {...this.props.Reason}
            className="card-reason"
            required={this.props.required}
            onUpdate={this.updateReason}
            onError={this.props.onError}
          />
        </Field>

        <Field
          attribute={`${attribute}.Amount`}
          title={i18n.t('financial.card.heading.amount')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <div>
            <Currency
              maxLength="10"
              min="1"
              attribute={`${attribute}.Amount`}
              name="Amount"
              {...this.props.Amount}
              className="card-amount"
              required={this.props.required}
              onUpdate={this.updateAmount}
              onError={this.props.onError}
            />
            <div className="flags">
              <Checkbox
                attribute={`${attribute}.AmountEstimated`}
                name="AmountEstimated"
                className="card-estimated"
                {...this.props.AmountEstimated}
                label={i18n.t('financial.card.label.estimated')}
                toggle="false"
                onUpdate={this.updateAmountEstimated}
                onError={this.props.onError}
              />
            </div>
          </div>
        </Field>

        <Field
          attribute={`${attribute}.Description`}
          title={i18n.t('financial.card.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          help="financial.card.help.description"
        >
          <Textarea
            attribute={`${attribute}.Description`}
            name="Description"
            {...this.props.Description}
            className="card-description"
            required={this.props.required}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
          />
        </Field>
      </div>
    );
  }
}

CardItem.defaultProps = {
  onUpdate: () => { },
  onError: (value, arr) => arr,
  required: false,
  requireFinancialCardDisciplinaryDate: true,
};
